import React, { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { MdAdd, MdDeleteOutline } from "react-icons/md";
import { PREVIEW_CANCELLATION_CHARGE } from "./graphql";
import { Card, FeeEditor, NumberInput, Row, Toggle, inputCls, rupees, setIn } from "./controls";

const TYPES = [
  { key: "STANDARD", label: "Standard" },
  { key: "BRIDAL", label: "Bridal" },
  { key: "PACKAGE", label: "Package" },
];

const EVENTS = [
  { value: "CUSTOMER_CANCEL", label: "Customer cancels" },
  { value: "NO_SHOW", label: "Customer no-show" },
  { value: "RESCHEDULE", label: "Customer reschedules" },
  { value: "BARBERA_CANCEL", label: "Barbera cancels" },
  { value: "BEAUTICIAN_NO_SHOW", label: "Beautician no-show" },
];

// Fees by booking type, plus a preview that runs the server's engine on the draft.
const PolicyRules = ({ policy, onChange }) => {
  const [type, setType] = useState("STANDARD");
  const rules = policy.rules[type];
  const set = (path, value) => onChange(setIn(policy, `rules.${type}.${path}`, value));
  const bands = [...(rules.lateCancel.bands || [])];

  return (
    <div className="grid xl:grid-cols-[1fr_360px] gap-5 items-start">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {TYPES.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setType(t.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${type === t.key ? "bg-[#FBB033] text-gray-900" : "bg-white/10 text-gray-200 hover:bg-white/20"}`}
            >
              {t.label}
            </button>
          ))}
          {type !== "STANDARD" && (
            <button
              type="button"
              onClick={() => window.confirm(`Replace all ${type.toLowerCase()} rules with the Standard ones?`) && onChange(setIn(policy, `rules.${type}`, JSON.parse(JSON.stringify(policy.rules.STANDARD))))}
              className="ml-auto text-xs text-gray-300 hover:text-white underline"
            >
              Copy from Standard
            </button>
          )}
        </div>
        <p className="text-xs text-gray-400">
          {type === "BRIDAL" && "Bridal = a booking with a service from the Bridal tab. "}
          {type === "PACKAGE" && "Package = a booking made with the package builder. "}
          Fees are worked out on the booking value (what the customer pays).
        </p>

        <Card
          title="Late cancellation (beautician not yet on the way)"
          hint="Cancelling earlier than every window below is free. The smallest window that covers the time left applies."
          right={<Toggle checked={rules.lateCancel.enabled} onChange={(v) => set("lateCancel.enabled", v)} />}
        >
          <div className={rules.lateCancel.enabled ? "" : "opacity-50 pointer-events-none"}>
            {bands.map((b, i) => (
              <Row key={i} label={<span className="inline-flex items-center gap-2">Within <NumberInput value={b.withinHours} width="w-16" step={0.5} onChange={(v) => set("lateCancel.bands", bands.map((x, j) => (j === i ? { ...x, withinHours: v } : x)))} /> hours of the slot</span>}>
                <FeeEditor fee={b.fee} onChange={(fee) => set("lateCancel.bands", bands.map((x, j) => (j === i ? { ...x, fee } : x)))} />
                {bands.length > 1 && (
                  <button type="button" title="Remove window" onClick={() => set("lateCancel.bands", bands.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500">
                    <MdDeleteOutline />
                  </button>
                )}
              </Row>
            ))}
            <button
              type="button"
              onClick={() => set("lateCancel.bands", [...bands, { withinHours: Math.max(1, Math.min(...bands.map((b) => Number(b.withinHours) || 1)) / 2), fee: { type: "FLAT", amount: 149 } }])}
              className="mt-2 text-xs font-semibold text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
            >
              <MdAdd /> Add a closer window (e.g. within 1 hour)
            </button>
          </div>
        </Card>

        <Card title="Beautician involved">
          <Row label="Beautician on the way" hint="They tapped “On the way”.">
            <Toggle checked={rules.onTheWay.enabled} onChange={(v) => set("onTheWay.enabled", v)} />
            <FeeEditor fee={rules.onTheWay.fee} disabled={!rules.onTheWay.enabled} onChange={(v) => set("onTheWay.fee", v)} />
          </Row>
          <Row label="Beautician at the door" hint="They tapped “Reached”.">
            <Toggle checked={rules.atDoor.enabled} onChange={(v) => set("atDoor.enabled", v)} />
            <FeeEditor fee={rules.atDoor.fee} disabled={!rules.atDoor.enabled} onChange={(v) => set("atDoor.fee", v)} />
          </Row>
          <Row label="Customer no-show" hint="Declined as Customer → No-show (incl. the nightly no-show sweep).">
            <Toggle checked={rules.noShow.enabled} onChange={(v) => set("noShow.enabled", v)} />
            <FeeEditor fee={rules.noShow.fee} disabled={!rules.noShow.enabled} onChange={(v) => set("noShow.fee", v)} />
          </Row>
        </Card>

        <Card title="Reschedule" hint="Moving the booking instead of cancelling.">
          <Row label="Free until" hint="Hours before the slot.">
            <NumberInput value={rules.reschedule.cutoffHours} suffix="hours before" width="w-16" step={0.5} onChange={(v) => set("reschedule.cutoffHours", v)} />
          </Row>
          <Row label="Late reschedule fee">
            <Toggle checked={rules.reschedule.lateFee.enabled} onChange={(v) => set("reschedule.lateFee.enabled", v)} />
            <FeeEditor fee={rules.reschedule.lateFee.fee} disabled={!rules.reschedule.lateFee.enabled} onChange={(v) => set("reschedule.lateFee.fee", v)} />
          </Row>
          <Row label="Free late reschedules" hint="Per customer, in any 90 days.">
            <NumberInput value={rules.reschedule.freeLatePer90Days} width="w-16" onChange={(v) => set("reschedule.freeLatePer90Days", v)} />
          </Row>
        </Card>
      </div>

      <Preview policy={policy} type={type} />
    </div>
  );
};

const H = 3600e3;

// Made-up cancellation run through the server's engine with the unsaved draft.
const Preview = ({ policy, type }) => {
  const [s, setS] = useState({
    event: "CUSTOMER_CANCEL",
    value: 1200,
    hoursBefore: 2,
    minutesSinceBooking: 600,
    beautician: "NOT_LEFT",
    lateMinutes: 0,
    firstLateCancel: false,
    feesThisMonth: 0,
  });
  const [run, { data, loading }] = useLazyQuery(PREVIEW_CANCELLATION_CHARGE, { fetchPolicy: "no-cache" });

  useEffect(() => {
    const t = setTimeout(() => {
      const at = new Date();
      const slotAt = new Date(at.getTime() + Number(s.hoursBefore || 0) * H);
      const late = Number(s.lateMinutes || 0);
      run({
        variables: {
          policy,
          scenario: {
            event: s.event,
            actor: "CUSTOMER",
            bookingType: type,
            bookingValue: Number(s.value) || 0,
            bookedAt: new Date(at.getTime() - Number(s.minutesSinceBooking || 0) * 60e3),
            slotAt,
            at,
            employeeAssigned: true,
            headedAt: s.beautician !== "NOT_LEFT" ? at : null,
            reachedAt: s.beautician === "AT_DOOR" ? new Date(slotAt.getTime() + late * 60e3) : null,
            completedBookings: 1,
            isFirstBooking: false,
            priorLateCancelTimes: s.firstLateCancel ? [] : [new Date(at.getTime() - 400 * 24 * H)],
            priorLateRescheduleTimes: [],
            feesThisMonth: Number(s.feesThisMonth) || 0,
          },
        },
      });
    }, 350);
    return () => clearTimeout(t);
  }, [policy, type, s, run]);

  const res = data?.previewCancellationCharge;
  const outcomes = res?.result?.outcomes || [];
  const set = (k) => (v) => setS((p) => ({ ...p, [k]: v }));

  return (
    <aside className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 xl:sticky xl:top-4 space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Try it</h3>
        <p className="text-xs text-gray-500">Runs your unsaved changes for a {type.toLowerCase()} booking.</p>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
        <label className="col-span-2">What happens
          <select value={s.event} onChange={(e) => set("event")(e.target.value)} className={`${inputCls} w-full mt-0.5`}>
            {EVENTS.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
          </select>
        </label>
        <label>Booking value<div className="mt-0.5"><NumberInput prefix="₹" value={s.value} width="w-full" onChange={set("value")} /></div></label>
        <label>Hours before slot<div className="mt-0.5"><NumberInput value={s.hoursBefore} width="w-full" step={0.25} onChange={set("hoursBefore")} /></div><span className="text-[10px] text-gray-400">Negative = after the slot</span></label>
        <label>Booked how long ago<div className="mt-0.5"><NumberInput value={s.minutesSinceBooking} suffix="min" width="w-full" onChange={set("minutesSinceBooking")} /></div></label>
        <label>Beautician
          <select value={s.beautician} onChange={(e) => set("beautician")(e.target.value)} className={`${inputCls} w-full mt-0.5`}>
            <option value="NOT_LEFT">Not left yet</option>
            <option value="ON_THE_WAY">On the way</option>
            <option value="AT_DOOR">At the door</option>
          </select>
        </label>
        {s.beautician === "AT_DOOR" && (
          <label>Arrived late by<div className="mt-0.5"><NumberInput value={s.lateMinutes} suffix="min" width="w-full" onChange={set("lateMinutes")} /></div></label>
        )}
        <label>Fees already this month<div className="mt-0.5"><NumberInput prefix="₹" value={s.feesThisMonth} width="w-full" onChange={set("feesThisMonth")} /></div></label>
        <label className="col-span-2 flex items-center gap-2 mt-1">
          <input type="checkbox" checked={s.firstLateCancel} onChange={(e) => set("firstLateCancel")(e.target.checked)} />
          Customer's first late cancellation
        </label>
      </div>

      <div className="border-t border-gray-100 pt-3 min-h-[80px]">
        {res?.status === "ERROR" && <p className="text-sm text-red-600">{res.message}</p>}
        {loading && !outcomes.length && <p className="text-sm text-gray-400">Working it out…</p>}
        {res?.status === "SUCCESS" && !outcomes.length && <p className="text-sm text-gray-500">Nothing to charge or credit.</p>}
        {outcomes.map((o, i) => (
          <div key={i} className={`rounded-lg p-3 mb-2 ${o.kind === "CREDIT" ? "bg-sky-50" : o.finalAmount > 0 ? "bg-amber-50" : "bg-emerald-50"}`}>
            <p className="text-lg font-semibold text-gray-900">
              {o.kind === "CREDIT" ? `Customer gets ${rupees(o.finalAmount)} credit` : o.finalAmount > 0 ? `Fee ${rupees(o.finalAmount)}` : "Free"}
              {o.kind === "FEE" && o.amount > 0 && o.finalAmount !== o.amount && <span className="text-sm font-normal text-gray-500 line-through ml-2">{rupees(o.amount)}</span>}
            </p>
            <ul className="mt-1 text-xs text-gray-600 list-disc pl-4 space-y-0.5">
              {(o.explanation || []).map((line, j) => <li key={j}>{line}</li>)}
            </ul>
          </div>
        ))}
      </div>
    </aside>
  );
};

export default PolicyRules;
